import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import RecentlyViewed, { addToRecentlyViewed } from '../components/RecentlyViewed';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RECENTLY_VIEWED_KEY = 'recentlyViewedNotecards';

const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { label: 'Shop', dropdown: SHOP_DROPDOWN('notecards') },
  CALENDAR_DROPDOWN(''),
  { to: '/archive', label: 'Archive' },
  { to: '/about', label: 'About' },
];

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

const NOTECARD_CATEGORIES = [
  'Best Selling',
  'Beachscapes',
  'Digital Art',
  'Dodging Shadows',
  'Flora & Fauna',
  'Landmarks',
  'OBX Sunrise',
  'Pelicans',
  'Ripples',
  'Waves',
];

// Pricing variants for all notecards
const NOTECARD_VARIANTS = [
  { id: 'single', label: 'Single', price: 6 },
  { id: 'six-pak', label: 'Six-Pak', price: 30 },
  { id: 'ten-pak', label: 'Ten-Pak', price: 40 }
];

function NotecardCard({ notecard, onView }) {
  const [selectedVariant, setSelectedVariant] = useState(NOTECARD_VARIANTS[0]);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const { addItem } = useCart();

  const trackView = () => {
    if (!hasTrackedView && onView) {
      onView(notecard);
      setHasTrackedView(true);
    }
  };

  const handleAddToCart = () => {
    trackView();
    addItem({
      shop: 'notecards',
      product_id: notecard.id,
      product_title: notecard.title,
      image_url: notecard.image_url,
      variant: selectedVariant.id,
      variant_label: selectedVariant.label,
      price: selectedVariant.price,
      quantity: 1,
      max_quantity: notecard.stock,
      special_instructions: specialInstructions || null
    });
    toast.success(`${notecard.title} added to cart!`);
  };

  const outOfStock = notecard.stock === 0;

  return (
    <div className="notecard-product-card" data-testid={`notecard-${notecard.id}`}>
      <div className="notecard-image-container" style={{ position: 'relative' }}>
        <img src={notecard.image_url} alt={notecard.title} className="notecard-image" />
        {outOfStock && (
          <div style={{ position: 'absolute', top: '10px', left: '10px', background: '#dc2626', color: '#fff', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}>
            Out of Stock
          </div>
        )}
      </div>
      <div className="notecard-details">
        <h3 className="notecard-title">{notecard.title}</h3>

        <div className="variant-selector">
          <label htmlFor={`variant-${notecard.id}`}>Quantity:</label>
          <select
            id={`variant-${notecard.id}`}
            value={selectedVariant.id}
            onChange={(e) => {
              const variant = NOTECARD_VARIANTS.find(v => v.id === e.target.value);
              setSelectedVariant(variant);
            }}
            className="variant-dropdown"
            data-testid={`variant-select-${notecard.id}`}
          >
            {NOTECARD_VARIANTS.map(variant => (
              <option key={variant.id} value={variant.id}>
                {variant.label} — ${variant.price}
              </option>
            ))}
          </select>
        </div>

        <div className="product-price">
          ${selectedVariant.price.toFixed(2)}
        </div>

        <div className="special-instructions">
          <label htmlFor={`instructions-${notecard.id}`}>
            Special Instructions or Personal Note (optional)
          </label>
          <textarea
            id={`instructions-${notecard.id}`}
            value={specialInstructions}
            onChange={(e) => setSpecialInstructions(e.target.value)}
            placeholder="Gift message, special requests, etc."
            className="instructions-textarea"
            rows={3}
          />
        </div>

        <Button
          className="buy-now-btn"
          onClick={handleAddToCart}
          disabled={outOfStock}
          data-testid={`buy-btn-${notecard.id}`}
        >
          <ShoppingCart size={18} />
          {outOfStock ? 'Out of Stock' : 'Add to Cart'}
        </Button>
      </div>
    </div>
  );
}

function Notecards() {
  const [notecards, setNotecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeSubcategory, setActiveSubcategory] = useState('All');
  const [expandedCategories, setExpandedCategories] = useState({});
  const { itemCount, openCart } = useCart();

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const selectCategory = (cat) => {
    setActiveCategory(cat);
    setActiveSubcategory('All');
  };

  const groupedNotecards = () => {
    const active = notecards.filter(n => n.active !== false && n.stock !== 0);
    if (activeCategory !== 'All') {
      let filtered = active.filter(n => n.category === activeCategory);
      if (activeSubcategory !== 'All') {
        filtered = filtered.filter(n => (n.subcategory || 'Unsorted') === activeSubcategory);
      }
      return { [activeCategory]: filtered };
    }
    const groups = {};
    active.forEach(n => {
      const cat = n.category || 'Uncategorized';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(n);
    });
    const sortedKeys = Object.keys(groups).sort((a, b) => {
      const ia = NOTECARD_CATEGORIES.indexOf(a);
      const ib = NOTECARD_CATEGORIES.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    const sorted = {};
    sortedKeys.forEach(k => { sorted[k] = groups[k]; });
    return sorted;
  };

  const availableCategories = ['All', ...NOTECARD_CATEGORIES.filter(cat =>
    notecards.some(n => n.category === cat && n.active !== false)
  )];

  // Only the active category needs a subcategory row, and only when it
  // actually has more than one subcategory worth splitting out.
  const availableSubcategories = activeCategory === 'All' ? [] : (() => {
    const subs = new Set(
      notecards
        .filter(n => n.category === activeCategory && n.active !== false && n.stock !== 0)
        .map(n => n.subcategory || 'Unsorted')
    );
    return subs.size > 1 ? ['All', ...[...subs].sort((a, b) => a === 'Unsorted' ? 1 : b === 'Unsorted' ? -1 : a.localeCompare(b))] : [];
  })();

  useEffect(() => {
    fetchNotecards();
    const stored = localStorage.getItem(RECENTLY_VIEWED_KEY);
    if (stored) {
      try {
        setRecentlyViewed(JSON.parse(stored));
      } catch (e) {}
    }
  }, []);

  const handleProductView = (notecard) => {
    const updated = addToRecentlyViewed(RECENTLY_VIEWED_KEY, notecard);
    setRecentlyViewed(updated);
  };

  const handleRecentItemClick = (item) => {
    const element = document.querySelector(`[data-testid="notecard-${item.id}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.style.boxShadow = '0 0 0 3px #1a1a1a';
      setTimeout(() => {
        element.style.boxShadow = '';
      }, 2000);
    }
  };

  const fetchNotecards = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notecards-products`);
      setNotecards(response.data);
    } catch (error) {
      console.error('Failed to fetch notecards:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="substack-site">
      {/* Header */}
      <Header navItems={NAV_ITEMS} cart={{ itemCount, onClick: openCart }} />

      {/* Hero Section */}
      <section className="notecards-hero" style={{ background: '#fff', padding: '50px 20px', textAlign: 'center' }}>
        <div className="hero-content" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <img
            src="https://daily-obx-cdn.b-cdn.net/newsletter-images/notecards.png"
            alt="Mostly Good Notecards by the Legendary OBX Beach Bum"
            style={{ maxWidth: '350px', width: '80%', height: 'auto', display: 'block', margin: '0 auto' }}
          />
        </div>
      </section>

      {/* Recently Viewed */}
      {recentlyViewed.length > 0 && !loading && (
        <RecentlyViewed
          storageKey={RECENTLY_VIEWED_KEY}
          onItemClick={handleRecentItemClick}
          title="Recently Viewed"
        />
      )}

      {/* Products Grid */}
      <main className="notecards-main">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p>Loading notecards...</p>
          </div>
        ) : notecards.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Mail size={64} style={{ color: '#ccc', marginBottom: '20px' }} />
            <h3 style={{ margin: '0 0 10px' }}>Coming Soon!</h3>
            <p style={{ color: '#666' }}>Our notecard collection is being prepared. Check back soon!</p>
          </div>
        ) : (
          <>
            {/* Category filter tabs */}
            {availableCategories.length > 1 && (
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '10px',
                justifyContent: 'center', padding: '0 20px 30px'
              }}>
                {availableCategories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => selectCategory(cat)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: '20px',
                      border: '2px solid',
                      borderColor: activeCategory === cat ? '#1a1a1a' : '#ddd',
                      background: activeCategory === cat ? '#1a1a1a' : '#fff',
                      color: activeCategory === cat ? '#fff' : '#444',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      fontWeight: activeCategory === cat ? '600' : '400',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Subcategory filter — only shown when the active category actually has more than one */}
            {availableSubcategories.length > 0 && (
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '8px',
                justifyContent: 'center', padding: '0 20px 30px'
              }}>
                {availableSubcategories.map(sub => (
                  <button
                    key={sub}
                    onClick={() => setActiveSubcategory(sub)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '16px',
                      border: '1px solid',
                      borderColor: activeSubcategory === sub ? '#7c3aed' : '#ddd',
                      background: activeSubcategory === sub ? '#f5f3ff' : '#fff',
                      color: activeSubcategory === sub ? '#7c3aed' : '#666',
                      fontFamily: 'inherit',
                      fontSize: '13px',
                      fontWeight: activeSubcategory === sub ? '600' : '400',
                      cursor: 'pointer'
                    }}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}

            {/* Grouped sections */}
            {Object.entries(groupedNotecards()).map(([category, cards]) => {
              if (cards.length === 0) return null;
              const showAsCollapsible = activeCategory === 'All';
              const expanded = !showAsCollapsible || !!expandedCategories[category];
              return (
                <div key={category} style={{ marginBottom: '48px' }}>
                  {showAsCollapsible && (
                    <button
                      onClick={() => toggleCategory(category)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: 'calc(100% - 40px)',
                        margin: '0 20px 20px',
                        padding: '0 0 10px',
                        border: 'none',
                        borderBottom: '2px solid #e0e0e0',
                        background: 'none',
                        cursor: 'pointer',
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontSize: '26px',
                        fontWeight: '600',
                        color: '#1a1a1a',
                        textAlign: 'left'
                      }}
                    >
                      <span>{category} <span style={{ fontSize: '16px', fontWeight: '400', color: '#888' }}>({cards.length})</span></span>
                      <span style={{ fontSize: '20px' }}>{expanded ? '▾' : '▸'}</span>
                    </button>
                  )}
                  <div className="notecards-grid">
                    {(expanded ? cards : cards.slice(0, 3)).map(notecard => (
                      <NotecardCard key={notecard.id} notecard={notecard} onView={handleProductView} />
                    ))}
                  </div>
                  {!expanded && cards.length > 3 && (
                    <button
                      onClick={() => toggleCategory(category)}
                      style={{
                        display: 'block',
                        margin: '15px 20px 0',
                        padding: '10px 20px',
                        border: '2px solid #ddd',
                        borderRadius: '20px',
                        background: '#fff',
                        color: '#444',
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Show all {cards.length} →
                    </button>
                  )}
                </div>
              );
            })}
          </>
        )}

        <div className="product-info-banner">
          <div className="info-item">
            <span className="info-icon">📐</span>
            <span>5x7 Size</span>
          </div>
          <div className="info-item">
            <span className="info-icon">✉️</span>
            <span>Envelope Included</span>
          </div>
          <div className="info-item">
            <span className="info-icon">📦</span>
            <span>Individually Wrapped</span>
          </div>
          <div className="info-item">
            <span className="info-icon">📝</span>
            <span>Blank Inside</span>
          </div>
        </div>

        <div className="shipping-info">
          <p>🚚 Free shipping on orders over $27 • US shipping only</p>
        </div>
      </main>

      {/* Footer */}
      <Footer links={FOOTER_LINKS} />
    </div>
  );
}

export default Notecards;
